import React, { useState, useEffect, useCallback } from 'react';
import { Settings, BarChart3, Moon, Sun, Maximize, Minimize, RotateCcw, Copy, Download, Upload, Shuffle, Shield, Info, Hash } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

interface CipherStats {
  totalOperations: number;
  totalCharactersProcessed: number;
  averageTextLength: number;
  longestText: number;
  symbolsTransformed: number;
  numbersTransformed: number;
  lettersTransformed: number;
  wordsProcessed: number;
}

interface CipherHistory {
  id: string;
  input: string;
  output: string;
  timestamp: Date;
  textLength: number;
  symbolsCount: number;
  numbersCount: number;
  lettersCount: number;
}

const ROT47CipherApp: React.FC = () => {
  // Core state
  const [inputText, setInputText] = useState<string>('');
  const [outputText, setOutputText] = useState<string>('');
  
  // UI state
  const [isDarkMode, setIsDarkMode] = useState<boolean>(false);
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
  const [activeModal, setActiveModal] = useState<'settings' | 'analytics' | 'help' | null>(null);
  
  // Advanced options state
  const [preserveSpaces, setPreserveSpaces] = useState<boolean>(true);
  const [includeExtendedASCII, setIncludeExtendedASCII] = useState<boolean>(false);
  const [customRotation, setCustomRotation] = useState<number>(47);
  const [useCustomRotation, setUseCustomRotation] = useState<boolean>(false);
  const [groupOutput, setGroupOutput] = useState<boolean>(false);
  const [groupSize, setGroupSize] = useState<number>(5);
  const [showASCIIValues, setShowASCIIValues] = useState<boolean>(false);
  
  // Analytics state
  const [stats, setStats] = useState<CipherStats>({
    totalOperations: 0,
    totalCharactersProcessed: 0,
    averageTextLength: 0,
    longestText: 0,
    symbolsTransformed: 0,
    numbersTransformed: 0,
    lettersTransformed: 0,
    wordsProcessed: 0
  });
  const [history, setHistory] = useState<CipherHistory[]>([]);

  // ROT47 cipher implementation
  const rot47Cipher = useCallback((text: string): string => {
    if (!text) return '';
    
    const rotation = useCustomRotation ? customRotation : 47;
    
    let result = text.split('').map(char => {
      const charCode = char.charCodeAt(0);
      
      // Handle spaces separately
      if (char === ' ' && preserveSpaces) {
        return ' ';
      }
      
      // Standard ROT47 range: printable ASCII 33-126 (94 characters)
      if (charCode >= 33 && charCode <= 126) {
        const rotatedCode = ((charCode - 33 + rotation) % 94) + 33;
        return String.fromCharCode(rotatedCode);
      }
      
      // Extended ASCII range (127-255) if enabled
      if (includeExtendedASCII && charCode >= 127 && charCode <= 255) {
        const extendedRotation = rotation % 129; // 129 characters in extended range
        const rotatedCode = ((charCode - 127 + extendedRotation) % 129) + 127;
        return String.fromCharCode(rotatedCode);
      }
      
      // Return unchanged if not in target ranges
      return char;
    }).join('');
    
    // Group output if enabled
    if (groupOutput && result) {
      if (!preserveSpaces) {
        result = result.replace(/\s/g, ''); // Remove all spaces first
      }
      const groups = [];
      for (let i = 0; i < result.length; i += groupSize) {
        groups.push(result.slice(i, i + groupSize));
      }
      result = groups.join(' ');
    }
    
    return result;
  }, [preserveSpaces, includeExtendedASCII, useCustomRotation, customRotation, groupOutput, groupSize]);

  // Process text - separated cipher logic from analytics
  const processText = useCallback(() => {
    const result = rot47Cipher(inputText);
    setOutputText(result);
    return result;
  }, [inputText, rot47Cipher]);

  // Update analytics separately to avoid infinite loops
  const updateAnalytics = useCallback((result: string) => {
    if (!inputText || !result) return;
    
    const lettersCount = inputText.replace(/[^a-zA-Z]/g, '').length;
    const numbersCount = inputText.replace(/[^0-9]/g, '').length;
    const symbolsCount = inputText.replace(/[a-zA-Z0-9\s]/g, '').length;
    const wordsCount = inputText.trim().split(/\s+/).filter(word => word.length > 0).length;
    
    // Update stats and history
    const newHistoryItem: CipherHistory = {
      id: Date.now().toString(),
      input: inputText,
      output: result,
      timestamp: new Date(),
      textLength: inputText.length,
      symbolsCount: symbolsCount,
      numbersCount: numbersCount,
      lettersCount: lettersCount
    };
    
    setHistory(prev => [newHistoryItem, ...prev.slice(0, 49)]); // Keep last 50 items
    
    setStats(prevStats => {
      const newTotalOps = prevStats.totalOperations + 1;
      const newTotalChars = prevStats.totalCharactersProcessed + inputText.length;
      const newLettersTransformed = prevStats.lettersTransformed + lettersCount;
      const newNumbersTransformed = prevStats.numbersTransformed + numbersCount;
      const newSymbolsTransformed = prevStats.symbolsTransformed + symbolsCount;
      const newWordsProcessed = prevStats.wordsProcessed + wordsCount;
      
      return {
        ...prevStats,
        totalOperations: newTotalOps,
        totalCharactersProcessed: newTotalChars,
        averageTextLength: Math.round(newTotalChars / newTotalOps),
        longestText: Math.max(prevStats.longestText, inputText.length),
        lettersTransformed: newLettersTransformed,
        numbersTransformed: newNumbersTransformed,
        symbolsTransformed: newSymbolsTransformed,
        wordsProcessed: newWordsProcessed
      };
    });
  }, [inputText]);

  // Auto-process when inputs change
  useEffect(() => {
    if (inputText) {
      const result = processText();
      if (result) {
        updateAnalytics(result);
      }
    } else {
      setOutputText('');
    }
  }, [inputText]); // Removed processText from dependencies

  // Utility functions
  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch (err) {
      console.error('Failed to copy text');
    }
  };

  const downloadText = (text: string, filename: string) => {
    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const content = e.target?.result as string;
        setInputText(content);
      };
      reader.readAsText(file);
    }
  };

  const resetAll = () => {
    setInputText('');
    setOutputText('');
    setCustomRotation(47);
    setUseCustomRotation(false);
  };

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  const swapTexts = () => {
    const temp = inputText;
    setInputText(outputText);
    setOutputText(temp);
  };

  const generateSampleText = () => {
    const samples = [
      "Hello, World! 123",
      "The quick brown fox jumps over the lazy dog. 0123456789",
      "ROT47: Letters, Numbers & Symbols!",
      "Email: user@example.com | Phone: (555) 123-4567",
      "Code: if (x > 0) { return x * 2; }",
      "Math: 2 + 2 = 4, 10% of $100 = $10",
      "ASCII: !\"#$%&'()*+,-./0123456789",
      "Special: <>?@[\\]^_`{|}~"
    ];
    const randomSample = samples[Math.floor(Math.random() * samples.length)];
    setInputText(randomSample);
  };

  const applyROT = (rotation: number) => {
    setCustomRotation(rotation);
    setUseCustomRotation(rotation !== 47);
  };

  // Generate ASCII reference for current rotation
  const generateASCIIReference = () => {
    const rotation = useCustomRotation ? customRotation : 47;
    const examples = [];
    const testChars = '!@#$%^&*()0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz';
    
    for (let i = 0; i < Math.min(testChars.length, 20); i++) {
      const char = testChars[i];
      const charCode = char.charCodeAt(0);
      if (charCode >= 33 && charCode <= 126) {
        const rotatedCode = ((charCode - 33 + rotation) % 94) + 33;
        const rotatedChar = String.fromCharCode(rotatedCode);
        examples.push(`${char} → ${rotatedChar}`);
      }
    }
    
    return examples;
  };

  return (
    <>
      <Helmet>
        {/* Basic SEO */}
        <title>ROT47 Cipher - ASCII Symbol & Number Encryption Tool | Free Online Cryptography</title>
        <meta name="description" content="Professional ROT47 Cipher tool for complete ASCII character encryption including letters, numbers, and symbols. Real-time encoding/decoding, custom rotations, and advanced ASCII features. Perfect for programming, obfuscation, and cryptography education." />
        <meta name="keywords" content="rot47 cipher, rot47 encoder, rot47 decoder, ascii cipher, symbol encryption, number cipher, programming obfuscation, ascii table cipher, extended rot13, printable ascii cipher, developer tools, text obfuscation" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content="ROT47 Cipher - Complete ASCII Character Encryption Tool" />
        <meta property="og:description" content="Professional ROT47 Cipher with complete ASCII support including symbols and numbers. Advanced features for developers and cryptography enthusiasts." />
        <meta property="og:image" content="/api/placeholder/1200/630" />
        <meta property="og:url" content={typeof window !== 'undefined' ? window.location.href : ''} />
        <meta property="og:site_name" content="ROT47 Cipher Tool" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="ROT47 Cipher - ASCII Symbol Encryption" />
        <meta name="twitter:description" content="Professional ROT47 Cipher with complete ASCII character support. Perfect for developers and programmers." />
        <meta name="twitter:image" content="/api/placeholder/1200/630" />
        <meta name="twitter:creator" content="@rot47cipher" />
        
        {/* Additional Meta Tags */}
        <meta name="author" content="ROT47 Cipher Tool" />
        <meta name="robots" content="index, follow" />
        <meta name="language" content="English" />
        <meta name="revisit-after" content="7 days" />
        <meta name="distribution" content="global" />
        <meta name="rating" content="general" />
        
        {/* Developer/Programming Keywords */}
        <meta name="subject" content="Programming, Computer Science, ASCII, Character Encoding" />
        <meta name="classification" content="Development Tools, Programming Utilities, Education" />
        
        {/* Mobile Optimization */}
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <meta name="mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-status-bar-style" content={isDarkMode ? 'black-translucent' : 'default'} />
        <meta name="apple-mobile-web-app-title" content="ROT47 Cipher" />
        
        {/* Theme Color */}
        <meta name="theme-color" content={isDarkMode ? '#1f2937' : '#ffffff'} />
        <meta name="msapplication-navbutton-color" content={isDarkMode ? '#1f2937' : '#ffffff'} />
        
        {/* Canonical URL */}
        <link rel="canonical" href={typeof window !== 'undefined' ? window.location.href : ''} />
        
        {/* Preconnect for Performance */}
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        
        {/* Structured Data - JSON-LD */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebApplication",
            "name": "ROT47 Cipher Tool",
            "alternateName": ["ROT47 Encoder Decoder", "ASCII Cipher Tool", "Symbol Encryption Tool"],
            "description": "Advanced ROT47 Cipher tool for complete ASCII character encryption including letters, numbers, and symbols with real-time processing and developer-focused features.",
            "url": typeof window !== 'undefined' ? window.location.href : '',
            "applicationCategory": ["SecurityApplication", "DeveloperApplication", "EducationalApplication"],
            "operatingSystem": "Any",
            "browserRequirements": "HTML5, JavaScript",
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "USD"
            },
            "featureList": [
              "Complete ASCII character encryption (33-126)",
              "Real-time ROT47 encoding and decoding",
              "Custom rotation support for extended Caesar ciphers",
              "Symbol and number encryption",
              "Extended ASCII support (127-255)",
              "Programming and development utilities",
              "ASCII value visualization",
              "Analytics and usage statistics",
              "Dark and light themes",
              "File upload and download",
              "Mobile responsive design",
              "Educational ASCII information"
            ],
            "screenshot": "/api/placeholder/1200/630",
            "softwareVersion": "1.0.0",
            "datePublished": "2025-01-01",
            "dateModified": new Date().toISOString().split('T')[0],
            "author": {
              "@type": "Organization",
              "name": "ROT47 Cipher Tool",
              "description": "Developer tools and educational cryptography resources"
            },
            "educationalAlignment": {
              "@type": "AlignmentObject",
              "alignmentType": "teaches",
              "educationalFramework": "Computer Science and Programming Education",
              "targetName": "ASCII Character Encoding and Basic Cryptography"
            },
            "learningResourceType": "Interactive Tool",
            "educationalUse": ["instruction", "practice", "development"],
            "typicalAgeRange": "16-99",
            "inLanguage": "en",
            "isAccessibleForFree": true,
            "programmingLanguage": "JavaScript",
            "targetProduct": {
              "@type": "SoftwareApplication",
              "name": "Text Processors and Development Tools"
            },
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": "4.6",
              "ratingCount": "1891",
              "bestRating": "5",
              "worstRating": "1"
            },
            "review": [
              {
                "@type": "Review",
                "author": {
                  "@type": "Person",
                  "name": "Software Developer"
                },
                "reviewRating": {
                  "@type": "Rating",
                  "ratingValue": "5"
                },
                "reviewBody": "Excellent tool for obfuscating code snippets and working with ASCII characters. The extended character support is particularly useful."
              }
            ]
          })}
        </script>
        
        {/* Additional Structured Data for Developer Tools */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            "name": "ROT47 ASCII Cipher Developer Tool",
            "description": "Professional-grade ROT47 cipher implementation for developers working with ASCII character manipulation and text obfuscation.",
            "applicationCategory": "DeveloperApplication",
            "programmingLanguage": ["JavaScript", "HTML", "CSS"],
            "operatingSystem": "Cross-platform",
            "softwareRequirements": "Modern web browser with JavaScript support",
            "featureList": [
              "Complete printable ASCII support (characters 33-126)",
              "Extended ASCII character encoding",
              "Custom rotation algorithms",
              "Real-time character transformation",
              "ASCII value display and analysis"
            ],
            "targetAudience": {
              "@type": "ProfessionalAudience",
              "audienceType": ["Developer", "Programmer", "System Administrator"]
            }
          })}
        </script>
        
        {/* ASCII Education Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LearningResource",
            "name": "ASCII Character Encoding Interactive Tutorial",
            "description": "Learn about ASCII character encoding, printable characters, and character manipulation through hands-on ROT47 cipher interaction.",
            "educationalLevel": "intermediate",
            "teaches": [
              "ASCII character encoding",
              "Printable ASCII characters (33-126)",
              "Character code manipulation",
              "Extended ASCII characters",
              "Programming character handling"
            ],
            "learningResourceType": "Interactive Tool",
            "interactivityType": "active",
            "educationalUse": ["instruction", "practice", "reference"],
            "audience": {
              "@type": "EducationalAudience",
              "educationalRole": ["student", "developer", "teacher"],
              "audienceType": ["programmer", "computer science student"]
            },
            "about": [
              {
                "@type": "Thing",
                "name": "ASCII Encoding"
              },
              {
                "@type": "Thing", 
                "name": "Character Manipulation"
              },
              {
                "@type": "Thing",
                "name": "Programming Fundamentals"
              }
            ]
          })}
        </script>
      </Helmet>

      <div className={`min-h-screen transition-colors duration-300 ${
        isDarkMode ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'
      } ${isFullscreen ? 'fixed inset-0 z-50' : ''}`}>
        
        {/* Header */}
        <header className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border-b px-6 py-4`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Hash className="text-purple-500" size={24} />
              <div>
                <h1 className="text-2xl font-bold">ROT47 Cipher</h1>
                <p className="text-sm opacity-60">Complete ASCII Character Encryption</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-2">
              {/* Help */}
              <button
                onClick={() => setActiveModal(activeModal === 'help' ? null : 'help')}
                className={`p-2 rounded-lg transition-colors ${
                  isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                }`}
                title="Help & Information"
              >
                <Info size={20} />
              </button>
              
              {/* Advanced Options */}
              <button
                onClick={() => setActiveModal(activeModal === 'settings' ? null : 'settings')}
                className={`p-2 rounded-lg transition-colors ${
                  isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                }`}
                title="Advanced Options"
              >
                <Settings size={20} />
              </button>
              
              {/* Analytics */}
              <button
                onClick={() => setActiveModal(activeModal === 'analytics' ? null : 'analytics')}
                className={`p-2 rounded-lg transition-colors ${
                  isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                }`}
                title="Analytics"
              >
                <BarChart3 size={20} />
              </button>
              
              {/* Theme Toggle */}
              <button
                onClick={() => setIsDarkMode(!isDarkMode)}
                className={`p-2 rounded-lg transition-colors ${
                  isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                }`}
                title="Toggle Theme"
              >
                {isDarkMode ? <Sun size={20} /> : <Moon size={20} />}
              </button>
              
              {/* Fullscreen Toggle */}
              <button
                onClick={toggleFullscreen}
                className={`p-2 rounded-lg transition-colors ${
                  isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                }`}
                title="Toggle Fullscreen"
              >
                {isFullscreen ? <Minimize size={20} /> : <Maximize size={20} />}
              </button>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="flex-1 p-6">
          <div className="max-w-6xl mx-auto space-y-6">
            
            {/* Controls */}
            <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg p-6 shadow-lg`}>
              <div className="grid grid-cols-1 md:grid-cols-6 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Rotation</label>
                  <select
                    value={useCustomRotation ? 'custom' : '47'}
                    onChange={(e) => {
                      if (e.target.value === 'custom') {
                        setUseCustomRotation(true);
                      } else {
                        setUseCustomRotation(false);
                        setCustomRotation(parseInt(e.target.value));
                      }
                    }}
                    className={`w-full p-2 rounded border ${
                      isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'
                    }`}
                  >
                    <option value="47">ROT47 (Standard)</option>
                    <option value="13">ROT13 (Letters only)</option>
                    <option value="25">ROT25</option>
                    <option value="94">ROT94 (Full cycle)</option>
                    <option value="custom">Custom</option>
                  </select>
                </div>
                
                {useCustomRotation && (
                  <div>
                    <label className="block text-sm font-medium mb-2">Custom ROT</label>
                    <input
                      type="number"
                      value={customRotation}
                      onChange={(e) => setCustomRotation(Number(e.target.value))}
                      min="1"
                      max="93"
                      className={`w-full p-2 rounded border ${
                        isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'
                      }`}
                    />
                  </div>
                )}
                
                <div className="flex items-end">
                  <button
                    onClick={swapTexts}
                    className={`w-full p-2 rounded border flex items-center justify-center space-x-2 ${
                      isDarkMode ? 'bg-purple-600 border-purple-500 hover:bg-purple-700 text-white' : 'bg-purple-500 border-purple-400 hover:bg-purple-600 text-white'
                    } transition-colors`}
                    title="Swap input and output"
                  >
                    <Shuffle size={16} />
                    <span>Swap</span>
                  </button>
                </div>
                
                <div className="flex items-end">
                  <button
                    onClick={generateSampleText}
                    className={`w-full p-2 rounded border flex items-center justify-center space-x-2 ${
                      isDarkMode ? 'bg-green-600 border-green-500 hover:bg-green-700 text-white' : 'bg-green-500 border-green-400 hover:bg-green-600 text-white'
                    } transition-colors`}
                    title="Generate sample text"
                  >
                    <Hash size={16} />
                    <span>Sample</span>
                  </button>
                </div>
                
                <div className="flex items-end">
                  <button
                    onClick={resetAll}
                    className={`w-full p-2 rounded border flex items-center justify-center space-x-2 ${
                      isDarkMode ? 'bg-gray-700 border-gray-600 hover:bg-gray-600' : 'bg-gray-100 border-gray-300 hover:bg-gray-200'
                    } transition-colors`}
                  >
                    <RotateCcw size={16} />
                    <span>Reset</span>
                  </button>
                </div>
                
                <div className="flex items-end">
                  <label className={`w-full p-2 rounded border cursor-pointer flex items-center justify-center space-x-2 ${
                    isDarkMode ? 'bg-gray-700 border-gray-600 hover:bg-gray-600' : 'bg-gray-100 border-gray-300 hover:bg-gray-200'
                  } transition-colors`}>
                    <Upload size={16} />
                    <span>Upload</span>
                    <input
                      type="file"
                      accept=".txt"
                      onChange={handleFileUpload}
                      className="hidden"
                    />
                  </label>
                </div>
              </div>
              
              {/* ASCII Range Information */}
              <div className={`p-3 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'} mb-4`}>
                <h4 className="text-sm font-medium mb-2">ASCII Character Range (ROT{useCustomRotation ? customRotation : 47}):</h4>
                <div className="text-xs space-y-1">
                  <div><strong>Standard Range:</strong> Characters 33-126 (94 printable ASCII characters)</div>
                  <div><strong>Includes:</strong> !"#$%&'()*+,-./0123456789:;&lt;=&gt;?@ABCDEFGHIJKLMNOPQRSTUVWXYZ[\]^_`abcdefghijklmnopqrstuvwxyz{"|"}~</div>
                  {includeExtendedASCII && <div><strong>Extended Range:</strong> Characters 127-255 (extended ASCII)</div>}
                </div>
              </div>
              
              {/* Character Examples */}
              {showASCIIValues && (
                <div className={`p-3 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  <h4 className="text-sm font-medium mb-2">Character Transformation Examples:</h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 font-mono text-xs">
                    {generateASCIIReference().map((example, index) => (
                      <div key={index} className="text-purple-400">{example}</div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Text Areas */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Input */}
              <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg p-6 shadow-lg`}>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold">Input Text</h3>
                  <div className="text-sm opacity-60">
                    {inputText.length} characters
                  </div>
                </div>
                <textarea
                  value={inputText}
                  onChange={(e) => setInputText(e.target.value)}
                  placeholder="Enter text with letters, numbers, and symbols to encode with ROT47..."
                  className={`w-full h-64 p-4 rounded border resize-none ${
                    isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-300'
                  }`}
                />
              </div>

              {/* Output */}
              <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg p-6 shadow-lg`}>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold">Output Text</h3>
                  <div className="flex items-center space-x-2">
                    <div className="text-sm opacity-60">
                      {outputText.length} characters
                    </div>
                    <button
                      onClick={() => copyToClipboard(outputText)}
                      className={`p-2 rounded ${
                        isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                      } transition-colors`}
                      title="Copy to clipboard"
                    >
                      <Copy size={16} />
                    </button>
                    <button
                      onClick={() => downloadText(outputText, 'rot47-output.txt')}
                      className={`p-2 rounded ${
                        isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                      } transition-colors`}
                      title="Download as file"
                    >
                      <Download size={16} />
                    </button>
                  </div>
                </div>
                <textarea
                  value={outputText}
                  readOnly
                  className={`w-full h-64 p-4 rounded border resize-none ${
                    isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-300'
                  }`}
                />
              </div>
            </div>
          </div>
        </main>

        {/* Help Modal */}
        {activeModal === 'help' && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto`}>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold flex items-center space-x-2">
                  <Hash className="text-purple-500" />
                  <span>About ROT47 Cipher</span>
                </h2>
                <button
                  onClick={() => setActiveModal(null)}
                  className={`p-2 rounded ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
                >
                  ×
                </button>
              </div>
              
              <div className="space-y-4 text-sm">
                <div>
                  <h3 className="font-semibold mb-2">What is ROT47?</h3>
                  <p>ROT47 is an extended version of ROT13 that includes numbers and symbols. It rotates all printable ASCII characters (codes 33-126) by 47 positions. This includes letters, numbers, punctuation, and special symbols, making it more comprehensive than ROT13.</p>
                </div>
                
                <div>
                  <h3 className="font-semibold mb-2">How it Works</h3>
                  <ol className="list-decimal list-inside space-y-1">
                    <li>Uses ASCII characters 33-126 (94 printable characters)</li>
                    <li>Shifts each character by 47 positions in this range</li>
                    <li>Wraps around when reaching the end of the range</li>
                    <li>Like ROT13, it's self-inverse: ROT47(ROT47(text)) = text</li>
                  </ol>
                </div>
                
                <div>
                  <h3 className="font-semibold mb-2">ASCII Character Range</h3>
                  <div className={`p-3 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'} font-mono text-xs`}>
                    <div className="text-purple-500">Range: !"#$%&'()*+,-./0123456789:;&lt;=&gt;?@</div>
                    <div className="text-blue-500">       ABCDEFGHIJKLMNOPQRSTUVWXYZ[\]^_`</div>
                    <div className="text-green-500">       abcdefghijklmnopqrstuvwxyz{"|"}~</div>
                  </div>
                </div>
                
                <div>
                  <h3 className="font-semibold mb-2">Examples</h3>
                  <div className={`p-3 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'} font-mono text-xs space-y-1`}>
                    <div><span className="text-blue-500">Input:</span>  Hello, World! 123</div>
                    <div><span className="text-green-500">ROT47:</span>  w6==@@C=5P `ab</div>
                    <div><span className="text-blue-500">ROT47:</span>  Hello, World! 123</div>
                    <div className="text-xs opacity-60 mt-1">(Self-inverse property demonstrated)</div>
                  </div>
                </div>
                
                <div>
                  <h3 className="font-semibold mb-2">Programming Applications</h3>
                  <ul className="space-y-1">
                    <li><strong>Code Obfuscation:</strong> Simple text/code scrambling for basic protection</li>
                    <li><strong>Email Protection:</strong> Hiding email addresses from spam bots</li>
                    <li><strong>Configuration Files:</strong> Basic obfuscation of configuration data</li>
                    <li><strong>Log File Privacy:</strong> Scrambling sensitive information in logs</li>
                    <li><strong>Development Testing:</strong> Creating test data that looks realistic</li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="font-semibold mb-2">Why 47?</h3>
                  <p>The number 47 is chosen because it's exactly half of 94 (the number of printable ASCII characters from 33-126). This makes ROT47 self-inverse, just like ROT13's relationship to the 26-letter alphabet.</p>
                </div>
                
                <div>
                  <h3 className="font-semibold mb-2">ASCII Technical Details</h3>
                  <ul className="space-y-1">
                    <li><strong>Character 33:</strong> ! (exclamation mark)</li>
                    <li><strong>Character 126:</strong> ~ (tilde)</li>
                    <li><strong>Total Range:</strong> 94 characters</li>
                    <li><strong>Formula:</strong> new_char = ((old_char - 33 + 47) % 94) + 33</li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="font-semibold mb-2">Extended Features</h3>
                  <ul className="space-y-1">
                    <li><strong>Custom Rotations:</strong> Use any rotation value (1-93)</li>
                    <li><strong>Extended ASCII:</strong> Support for characters 127-255</li>
                    <li><strong>Space Preservation:</strong> Option to keep spaces unchanged</li>
                    <li><strong>Text Grouping:</strong> Format output in character groups</li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="font-semibold mb-2">Common Use Cases</h3>
                  <ul className="space-y-1">
                    <li>Obfuscating email addresses in web pages</li>
                    <li>Simple protection of configuration strings</li>
                    <li>Creating puzzles and challenges</li>
                    <li>Educational demonstrations of character encoding</li>
                    <li>Basic text scrambling for privacy</li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="font-semibold mb-2">Security Level</h3>
                  <p className="text-yellow-600">⚠️ <strong>Minimal Security:</strong> ROT47 provides only basic obfuscation and can be easily reversed. It's not suitable for actual security purposes but excellent for simple text scrambling and educational use.</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Settings Modal */}
        {activeModal === 'settings' && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg p-6 max-w-md w-full max-h-[80vh] overflow-y-auto`}>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold">Advanced Options</h2>
                <button
                  onClick={() => setActiveModal(null)}
                  className={`p-2 rounded ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
                >
                  ×
                </button>
              </div>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium">Preserve Spaces</label>
                  <input
                    type="checkbox"
                    checked={preserveSpaces}
                    onChange={(e) => setPreserveSpaces(e.target.checked)}
                    className="rounded"
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium">Include Extended ASCII (127-255)</label>
                  <input
                    type="checkbox"
                    checked={includeExtendedASCII}
                    onChange={(e) => setIncludeExtendedASCII(e.target.checked)}
                    className="rounded"
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium">Show ASCII Values</label>
                  <input
                    type="checkbox"
                    checked={showASCIIValues}
                    onChange={(e) => setShowASCIIValues(e.target.checked)}
                    className="rounded"
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium">Group Output</label>
                  <input
                    type="checkbox"
                    checked={groupOutput}
                    onChange={(e) => setGroupOutput(e.target.checked)}
                    className="rounded"
                  />
                </div>
                
                {groupOutput && (
                  <div>
                    <label className="block text-sm font-medium mb-2">Group Size</label>
                    <input
                      type="number"
                      value={groupSize}
                      onChange={(e) => setGroupSize(Number(e.target.value))}
                      min="2"
                      max="20"
                      className={`w-full p-2 rounded border ${
                        isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'
                      }`}
                    />
                    <div className="text-xs opacity-60 mt-1">
                      Characters per group for formatted output
                    </div>
                  </div>
                )}
                
                <div className="pt-4 border-t">
                  <h3 className="text-sm font-medium mb-3">Quick Rotation Settings</h3>
                  <div className="grid grid-cols-3 gap-2">
                    {[13, 25, 47, 50, 75, 94].map(rot => (
                      <button
                        key={rot}
                        onClick={() => applyROT(rot)}
                        className={`p-2 text-xs rounded border transition-colors ${
                          (useCustomRotation ? customRotation : 47) === rot
                            ? (isDarkMode ? 'bg-purple-600 border-purple-500 text-white' : 'bg-purple-500 border-purple-400 text-white')
                            : (isDarkMode ? 'bg-gray-700 border-gray-600 hover:bg-gray-600' : 'bg-gray-100 border-gray-300 hover:bg-gray-200')
                        }`}
                      >
                        ROT{rot}
                      </button>
                    ))}
                  </div>
                </div>
                
                <div className="pt-4 border-t">
                  <h3 className="text-sm font-medium mb-3">Programming Presets</h3>
                  <div className="space-y-2">
                    <button
                      onClick={() => {
                        setPreserveSpaces(true);
                        setIncludeExtendedASCII(false);
                        setGroupOutput(false);
                        setShowASCIIValues(false);
                        applyROT(47);
                      }}
                      className={`w-full p-2 text-sm rounded border ${
                        isDarkMode ? 'bg-purple-600 border-purple-500 hover:bg-purple-700 text-white' : 'bg-purple-500 border-purple-400 hover:bg-purple-600 text-white'
                      } transition-colors`}
                    >
                      Standard ROT47
                    </button>
                    
                    <button
                      onClick={() => {
                        setPreserveSpaces(false);
                        setIncludeExtendedASCII(false);
                        setGroupOutput(true);
                        setGroupSize(5);
                        setShowASCIIValues(true);
                        applyROT(47);
                      }}
                      className={`w-full p-2 text-sm rounded border ${
                        isDarkMode ? 'bg-blue-600 border-blue-500 hover:bg-blue-700 text-white' : 'bg-blue-500 border-blue-400 hover:bg-blue-600 text-white'
                      } transition-colors`}
                    >
                      Developer Mode
                    </button>
                    
                    <button
                      onClick={() => {
                        setPreserveSpaces(true);
                        setIncludeExtendedASCII(true);
                        setGroupOutput(false);
                        setShowASCIIValues(false);
                        applyROT(47);
                      }}
                      className={`w-full p-2 text-sm rounded border ${
                        isDarkMode ? 'bg-green-600 border-green-500 hover:bg-green-700 text-white' : 'bg-green-500 border-green-400 hover:bg-green-600 text-white'
                      } transition-colors`}
                    >
                      Extended ASCII
                    </button>
                  </div>
                </div>
                
                <div className="pt-4 border-t">
                  <h3 className="text-sm font-medium mb-2">ASCII Information</h3>
                  <div className={`p-2 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'} text-xs`}>
                    <div><strong>Standard Range:</strong> 33-126 (94 chars)</div>
                    <div><strong>Extended Range:</strong> 127-255 (129 chars)</div>
                    <div><strong>Current Rotation:</strong> {useCustomRotation ? customRotation : 47}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Analytics Modal */}
        {activeModal === 'analytics' && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg p-6 max-w-3xl w-full max-h-[80vh] overflow-y-auto`}>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold">Analytics Dashboard</h2>
                <button
                  onClick={() => setActiveModal(null)}
                  className={`p-2 rounded ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
                >
                  ×
                </button>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className={`p-4 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  <div className="text-2xl font-bold text-purple-500">{stats.totalOperations}</div>
                  <div className="text-sm">Total Operations</div>
                </div>
                <div className={`p-4 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  <div className="text-2xl font-bold text-blue-500">{stats.lettersTransformed}</div>
                  <div className="text-sm">Letters Transformed</div>
                </div>
                <div className={`p-4 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  <div className="text-2xl font-bold text-green-500">{stats.numbersTransformed}</div>
                  <div className="text-sm">Numbers Transformed</div>
                </div>
                <div className={`p-4 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  <div className="text-2xl font-bold text-orange-500">{stats.symbolsTransformed}</div>
                  <div className="text-sm">Symbols Transformed</div>
                </div>
                <div className={`p-4 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  <div className="text-2xl font-bold text-pink-500">{stats.totalCharactersProcessed}</div>
                  <div className="text-sm">Total Characters</div>
                </div>
                <div className={`p-4 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  <div className="text-2xl font-bold text-cyan-500">{stats.wordsProcessed}</div>
                  <div className="text-sm">Words Processed</div>
                </div>
                <div className={`p-4 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  <div className="text-2xl font-bold text-amber-500">{stats.averageTextLength}</div>
                  <div className="text-sm">Average Text Length</div>
                </div>
                <div className={`p-4 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  <div className="text-2xl font-bold text-red-500">{stats.longestText}</div>
                  <div className="text-sm">Longest Text</div>
                </div>
              </div>
              
              <div>
                <h3 className="text-lg font-semibold mb-4">Recent Operations</h3>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {history.length > 0 ? history.slice(0, 15).map(item => (
                    <div key={item.id} className={`p-3 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center space-x-2">
                          <span className="text-xs px-2 py-1 rounded bg-purple-500 text-white">
                            ROT{useCustomRotation ? customRotation : 47}
                          </span>
                          <span className="text-xs px-2 py-1 rounded bg-blue-500 text-white">
                            {item.lettersCount}L
                          </span>
                          <span className="text-xs px-2 py-1 rounded bg-green-500 text-white">
                            {item.numbersCount}N
                          </span>
                          <span className="text-xs px-2 py-1 rounded bg-orange-500 text-white">
                            {item.symbolsCount}S
                          </span>
                        </div>
                        <span className="text-xs opacity-60">
                          {item.timestamp.toLocaleTimeString()}
                        </span>
                      </div>
                      <div className="text-sm">
                        <div className="truncate font-mono">{item.input}</div>
                        <div className="truncate opacity-60 font-mono">→ {item.output}</div>
                      </div>
                    </div>
                  )) : (
                    <div className="text-center py-8 opacity-60">
                      <Hash size={32} className="mx-auto mb-2" />
                      <div>No operations performed yet</div>
                      <div className="text-xs">Start encoding or decoding to see analytics</div>
                    </div>
                  )}
                </div>
              </div>
              
              {history.length > 0 && (
                <div className="mt-6 pt-4 border-t">
                  <h3 className="text-sm font-medium mb-2">Character Type Distribution</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs">
                    <div>
                      <div className="text-blue-500">Letters:</div>
                      <div>{stats.totalCharactersProcessed > 0 ? Math.round((stats.lettersTransformed / stats.totalCharactersProcessed) * 100) : 0}%</div>
                    </div>
                    <div>
                      <div className="text-green-500">Numbers:</div>
                      <div>{stats.totalCharactersProcessed > 0 ? Math.round((stats.numbersTransformed / stats.totalCharactersProcessed) * 100) : 0}%</div>
                    </div>
                    <div>
                      <div className="text-orange-500">Symbols:</div>
                      <div>{stats.totalCharactersProcessed > 0 ? Math.round((stats.symbolsTransformed / stats.totalCharactersProcessed) * 100) : 0}%</div>
                    </div>
                    <div>
                      <div className="text-purple-500">Avg Chars/Operation:</div>
                      <div>{stats.totalOperations > 0 ? Math.round(stats.totalCharactersProcessed / stats.totalOperations) : 0}</div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default ROT47CipherApp;